import { useState } from "react";
import { Check, Plus } from "lucide-react";
import { Button, Input, Modal, toast } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import s from "../shared.module.css";

/** Add one or many people to a static group, or create a new group with them. */
export function GroupPickerModal({ open, onClose, contactIds }: { open: boolean; onClose: () => void; contactIds: string[] }) {
  const groups = useEngineQuery("listGroups", undefined, { enabled: open });
  const current = useEngineQuery("getGroupsForContact", { contactId: contactIds[0] ?? "" }, { enabled: open && contactIds.length === 1 });
  const add = useEngineMutation("addContactsToGroup");
  const remove = useEngineMutation("removeContactsFromGroup");
  const create = useEngineMutation("createGroup");
  const [filter, setFilter] = useState("");
  const [newName, setNewName] = useState("");
  const statics = (groups.data ?? []).filter((g) => g.kind === "static" && !g.isInternal && g.name.toLowerCase().includes(filter.toLowerCase()));
  const memberOf = new Set((current.data ?? []).map((g) => g.id));

  return (
    <Modal open={open} onClose={onClose} title={contactIds.length === 1 ? "Add to group" : `Add ${contactIds.length} people to a group`}>
      <div className="u-col" style={{ gap: 10 }}>
        <Input autoFocus placeholder="Filter groups" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <div style={{ maxHeight: 280, overflowY: "auto" }}>
          {statics.map((g) => {
            const on = memberOf.has(g.id);
            return (
              <button
                key={g.id}
                type="button"
                className={s.chip}
                data-size="md"
                style={{ display: "flex", width: "100%", justifyContent: "flex-start", marginBottom: 4, background: on ? "var(--bg-selected)" : undefined }}
                onClick={async () => {
                  if (on && contactIds.length === 1) {
                    await remove.mutateAsync({ groupId: g.id, contactIds });
                    toast(`Removed from ${g.name}`);
                  } else {
                    await add.mutateAsync({ groupId: g.id, contactIds });
                    toast(`Added to ${g.name}`, "success");
                    if (contactIds.length > 1) onClose();
                  }
                }}
              >
                <span style={{ width: 12, height: 12, borderRadius: 3, background: g.color ?? "var(--text-tertiary)", marginLeft: 6 }} />
                <span className="u-grow" style={{ textAlign: "left" }}>{g.name}</span>
                <span className="u-small u-muted">{g.memberCount}</span>
                {on && <Check size={14} />}
              </button>
            );
          })}
          {groups.isSuccess && statics.length === 0 && <div className="u-muted u-small" style={{ padding: 8 }}>No static groups match. Smart groups fill themselves from their filter.</div>}
        </div>
        <div className="u-row">
          <Input placeholder="New group name" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && newName.trim() && create.mutate({ name: newName.trim(), contactIds }, { onSuccess: () => { toast(`Created ${newName.trim()}`, "success"); onClose(); } })} />
          <Button icon={<Plus size={14} />} disabled={!newName.trim()} loading={create.isPending} onClick={() => create.mutate({ name: newName.trim(), contactIds }, { onSuccess: () => { toast(`Created ${newName.trim()}`, "success"); onClose(); } })}>
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}
